import React, { FC } from 'react'
import ReactDOM from 'react-dom'
import { CalendarClock } from 'lucide-react'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from 'components/Chat/helper'
import { formatMinute } from '../../helpers'
import type { IBlockInfo } from '../../types'

interface BlockDetailModalProps {
  block: IBlockInfo | null
  onClose: () => void
}

export const BlockDetailModal: FC<BlockDetailModalProps> = ({ block, onClose }) => {
  if (!block) return null

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full transform transition-all duration-200 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with color accent */}
        <div
          className="px-6 py-4 rounded-t-lg border-l-4"
          style={{
            borderLeftColor: block.color,
            backgroundColor: `${block.color}10`,
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-primary-text mb-1">
                {block.title || 'Busy'}
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CalendarClock className="w-4 h-4" />
                <span>
                  {block.date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded transition-colors">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Time */}
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${block.color}20` }}
            >
              <svg
                className="w-5 h-5"
                style={{ color: block.color }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Time</p>
              <p className="text-base text-primary-text">
                {block.startHour === 0
                  ? '12'
                  : block.startHour > 12
                  ? block.startHour - 12
                  : block.startHour}
                :{formatMinute(block.startMinute)}
                {block.startHour < 12 ? ' AM' : ' PM'}
                {' - '}
                {block.endHour === 0
                  ? '12'
                  : block.endHour > 12
                  ? block.endHour - 12
                  : block.endHour}
                :{formatMinute(block.endMinute)}
                {block.endHour < 12 ? ' AM' : ' PM'}
              </p>
            </div>
          </div>

          {/* Attendee */}
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${block.color}20` }}
            >
              {block.attendee.value.includes('@') ? (
                <svg
                  className="w-5 h-5"
                  style={{ color: block.color }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              ) : (
                <AppAvatar
                  size="sm"
                  type="mixed"
                  shape="Rounded"
                  text={getInitials({
                    firstName: block.attendee.user?.firstName || '',
                    lastName: block.attendee.user?.lastName || '',
                  })}
                  colorSeed={block.attendee.label || block.attendee.value}
                  darker
                />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Attendee</p>
              <p className="text-base text-primary-text">{block.attendee.label}</p>
            </div>
          </div>

          {/* Color Badge */}
          <div className="flex items-center gap-2 pt-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: block.color }} />
            <span className="text-xs text-gray-500">
              {block.attendee.value.includes('@') ? 'External Guest' : 'Team Member'}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
